import { FC } from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { useMutation } from 'react-relay';
import { StudentsResetDiagnosticMutation } from '../../graphQLOperations/__generated__/StudentsResetDiagnosticMutation.graphql';
import { resetDiagnosticMutation } from '../../graphQLOperations/Students';
import { useAppDispatch } from '../../redux/hooks';
import { setDiagnosticResetKey } from '../../redux/reducers/diagnosticResetSlice';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  visible: boolean;
  onHide: () => void;
  studentName: string;
  publicUserId: string | null | undefined;
  diagnosticId: string;
  studentId: string;
}

const ResetDiagnosticDialog: FC<Props> = ({ visible, onHide, studentName, publicUserId, diagnosticId, studentId }) => {
  const [resetDiagnostic] = useMutation<StudentsResetDiagnosticMutation>(resetDiagnosticMutation);
  const dispatch = useAppDispatch();

  const handleReset = () => {
    resetDiagnostic({
      variables: {
        studentId,
        diagnosticId,
      },
      onCompleted: () => {
        // Bump the reset key so TeacherDashboard refetches getDiagnosticStatusReport
        // (network-only) and the chart, cards and modals reflect the new state.
        dispatch(setDiagnosticResetKey(new Date().getTime()));
        onHide();
      },
      onError: error => {
        console.error('Error resetting diagnostic:', error);
      },
    });
  };

  return (
    <ConfirmDialog
      tagKey="reset-diagnostic"
      visible={visible}
      onHide={onHide}
      closable={false}
      className="!tw-max-w-[440px]"
      content={({ hide }) => (
        <div className="tw-flex tw-flex-col tw-gap-y-3 tw-p-6 tw-bg-white tw-rounded-2xl">
          <h1 className="tw-text-2xl tw-font-bold tw-font-atkinson tw-text-neutral-950 tw-m-0">
            Reset Diagnostic for{' '}
            <MaskedStudentData student={{ publicUserId }} type={PiiTypes.FullName}>
              {studentName}
            </MaskedStudentData>
            ?
          </h1>

          <p className="tw-text-sm tw-font-atkinson tw-text-neutral-700 tw-m-0">
            This will erase their previous diagnostic results and they&apos;ll need to start over.
          </p>

          <div className="tw-flex tw-justify-end tw-gap-x-2 tw-mt-2">
            <Button label="Cancel" outlined onClick={e => hide(e)} />
            <Button
              label="Reset diagnostic"
              onClick={e => {
                handleReset();
                hide(e);
              }}
            />
          </div>
        </div>
      )}
    />
  );
};

export default ResetDiagnosticDialog;
